import React, { useState } from 'react';
import { Input, Stack, FormLabel, Tooltip, Button, Typography, Modal, ModalDialog, ModalClose, DialogTitle, Divider, IconButton, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemContent, Checkbox, Box, Chip, Select, Option } from '@mui/joy';
import { Add, Delete, Edit, ToggleOff, ToggleOn, ContentCopy } from '@mui/icons-material';
import PDGSearchbar from './PDGSearchbar';
import ObjectCacheSelector from './ObjectCacheSelector';
import { useObjectCache } from '../hooks/useObjectCache';
import { getDefaultValuesForSchema, getDiscriminatedUnionDefault } from '../Internal/schemaDefaults';

export default function Parameter(props) {
    const { paramKey, paramSchema, value, onChange, level = 0 } = props;
    const [objectModal, setObjectModal] = useState(false);
    const [arrayItemModal, setArrayItemModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [tempObjectValue, setTempObjectValue] = useState({});
    
    // State for simple array input
    const [addValueModal, setAddValueModal] = useState(false);
    const [editingValueIndex, setEditingValueIndex] = useState(null);
    const [tempValue, setTempValue] = useState('');

    // State for cache selector
    const [cacheSelectorOpen, setCacheSelectorOpen] = useState(false);
    const { getSchemaKey, autoCacheObject } = useObjectCache();

    // Handle simple number/integer types
    const renderNumberInput = () => {
        const isOptional = paramSchema.optional === true;
        const isNull = value === null;
        
        return (
            <Box 
                key={paramKey} 
                sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: isOptional && isNull ? 'neutral.300' : 'neutral.200',
                    borderRadius: 'md',
                    backgroundColor: isOptional && isNull ? 'neutral.50' : 'background.surface',
                    transition: 'all 0.2s ease-in-out',
                    opacity: isOptional && isNull ? 0.6 : 1
                }}
            >
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={paramSchema.description || ''}>
                                <FormLabel sx={{ fontWeight: 'md' }}>
                                    {paramKey.replace(/_/g, ' ')}
                                </FormLabel>
                            </Tooltip>
                            {isOptional && (
                                <Chip 
                                    size="sm" 
                                    variant="soft" 
                                    color={isNull ? 'neutral' : 'success'}
                                    startDecorator={isNull ? <ToggleOff /> : <ToggleOn />}
                                >
                                    {isNull ? 'Optional' : 'Enabled'}
                                </Chip>
                            )}
                        </Stack>
                        {isOptional && (
                            <Button
                                size="sm"
                                variant={isNull ? "soft" : "solid"}
                                color={isNull ? "neutral" : "primary"}
                                startDecorator={isNull ? <ToggleOn /> : <ToggleOff />}
                                onClick={() => {
                                    if (isNull) {
                                        // Set to default value when enabling
                                        const defaultValue = paramSchema.default !== undefined && paramSchema.default !== "PydanticUndefined" 
                                            ? (paramSchema.default.value !== undefined ? paramSchema.default.value : paramSchema.default)
                                            : 0;
                                        onChange(defaultValue);
                                    } else {
                                        // Set to null when disabling
                                        onChange(null);
                                    }
                                }}
                                sx={{ minWidth: 'auto' }}
                            >
                                {isNull ? 'Enable' : 'Disable'}
                            </Button>
                        )}
                    </Stack>
                    
                    {(!isOptional || !isNull) && (
                        <Input
                            type="number"
                            value={value || 0}
                            onChange={(e) => {
                                const newValue = paramSchema.type === 'integer' 
                                    ? parseInt(e.target.value) || 0 
                                    : parseFloat(e.target.value) || 0;
                                onChange(newValue);
                            }}
                            slotProps={{
                                input: {
                                    step: paramSchema.type === 'integer' ? 1 : 0.01
                                }
                            }}
                            disabled={isOptional && isNull}
                            sx={{
                                '& .MuiInput-input': {
                                    fontWeight: 'md'
                                }
                            }}
                        />
                    )}
                    
                    {isOptional && isNull && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            This parameter is optional and currently disabled
                        </Typography>
                    )}
                    
                    {paramSchema.description && (
                        <Typography level="body-xs" color="neutral">
                            {paramSchema.description}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    // Handle boolean type
    const renderBooleanInput = () => {
        const isOptional = paramSchema.optional === true;
        const isNull = value === null;
        
        return (
            <Box 
                key={paramKey} 
                sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: isOptional && isNull ? 'neutral.300' : 'neutral.200',
                    borderRadius: 'md',
                    backgroundColor: isOptional && isNull ? 'neutral.50' : 'background.surface',
                    transition: 'all 0.2s ease-in-out',
                    opacity: isOptional && isNull ? 0.6 : 1
                }}
            >
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={paramSchema.description || ''}>
                                <FormLabel sx={{ fontWeight: 'md' }}>
                                    {paramKey.replace(/_/g, ' ')}
                                </FormLabel>
                            </Tooltip>
                            {isOptional && (
                                <Chip 
                                    size="sm" 
                                    variant="soft" 
                                    color={isNull ? 'neutral' : 'success'}
                                    startDecorator={isNull ? <ToggleOff /> : <ToggleOn />}
                                >
                                    {isNull ? 'Optional' : 'Enabled'}
                                </Chip>
                            )}
                        </Stack>
                        {isOptional && (
                            <Button
                                size="sm"
                                variant={isNull ? "soft" : "solid"}
                                color={isNull ? "neutral" : "primary"}
                                startDecorator={isNull ? <ToggleOn /> : <ToggleOff />}
                                onClick={() => {
                                    if (isNull) {
                                        // Set to default value when enabling
                                        const defaultValue = paramSchema.default !== undefined && paramSchema.default !== "PydanticUndefined" 
                                            ? (paramSchema.default.value !== undefined ? paramSchema.default.value : paramSchema.default)
                                            : false;
                                        onChange(defaultValue);
                                    } else {
                                        // Set to null when disabling
                                        onChange(null);
                                    }
                                }}
                                sx={{ minWidth: 'auto' }}
                            >
                                {isNull ? 'Enable' : 'Disable'}
                            </Button>
                        )}
                    </Stack>
                    
                    {(!isOptional || !isNull) && (
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant={value === true ? "solid" : "outlined"}
                                color={value === true ? "success" : "neutral"}
                                startDecorator={value === true ? <ToggleOn /> : <ToggleOff />}
                                onClick={() => onChange(!value)}
                                disabled={isOptional && isNull}
                                sx={{ 
                                    minWidth: 120,
                                    fontWeight: 'md'
                                }}
                            >
                                {value === true ? 'True' : 'False'}
                            </Button>
                        </Stack>
                    )}
                    
                    {isOptional && isNull && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            This parameter is optional and currently disabled
                        </Typography>
                    )}
                    
                    {paramSchema.description && (
                        <Typography level="body-xs" color="neutral">
                            {paramSchema.description}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    // Handle string type
    const renderStringInput = () => {
        const isOptional = paramSchema.optional === true;
        const isNull = value === null;
        
        return (
            <Box 
                key={paramKey} 
                sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: isOptional && isNull ? 'neutral.300' : 'neutral.200',
                    borderRadius: 'md',
                    backgroundColor: isOptional && isNull ? 'neutral.50' : 'background.surface',
                    transition: 'all 0.2s ease-in-out',
                    opacity: isOptional && isNull ? 0.6 : 1
                }}
            >
                <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={paramSchema.description || ''}>
                                <FormLabel sx={{ fontWeight: 'md' }}>
                                    {paramKey.replace(/_/g, ' ')}
                                </FormLabel>
                            </Tooltip>
                            {isOptional && (
                                <Chip 
                                    size="sm" 
                                    variant="soft" 
                                    color={isNull ? 'neutral' : 'success'}
                                    startDecorator={isNull ? <ToggleOff /> : <ToggleOn />}
                                >
                                    {isNull ? 'Optional' : 'Enabled'}
                                </Chip>
                            )}
                        </Stack>
                        {isOptional && (
                            <Button
                                size="sm"
                                variant={isNull ? "soft" : "solid"}
                                color={isNull ? "neutral" : "primary"}
                                startDecorator={isNull ? <ToggleOn /> : <ToggleOff />}
                                onClick={() => {
                                    if (isNull) {
                                        // Set to default value when enabling
                                        const defaultValue = paramSchema.default !== undefined && paramSchema.default !== "PydanticUndefined" 
                                            ? (paramSchema.default.value !== undefined ? paramSchema.default.value : paramSchema.default)
                                            : '';
                                        onChange(defaultValue);
                                    } else {
                                        // Set to null when disabling
                                        onChange(null);
                                    }
                                }}
                                sx={{ minWidth: 'auto' }}
                            >
                                {isNull ? 'Enable' : 'Disable'}
                            </Button>
                        )}
                    </Stack>
                    
                    {(!isOptional || !isNull) && (
                        <Input
                            value={value || ''}
                            onChange={(e) => onChange(e.target.value)}
                            disabled={isOptional && isNull}
                            sx={{
                                '& .MuiInput-input': {
                                    fontWeight: 'md'
                                }
                            }}
                        />
                    )}
                    
                    {isOptional && isNull && (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            This parameter is optional and currently disabled
                        </Typography>
                    )}
                    
                    {paramSchema.description && (
                        <Typography level="body-xs" color="neutral">
                            {paramSchema.description}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    // Handle simple array types (array of numbers/strings)
    const renderSimpleArrayInput = () => {
        const arrayValue = Array.isArray(value) ? value : [];

        const handleAddValue = () => {
            setTempValue('');
            setEditingValueIndex(null);
            setAddValueModal(true);
        };

        const handleEditValue = (index) => {
            setTempValue(arrayValue[index]?.toString() || '');
            setEditingValueIndex(index);
            setAddValueModal(true);
        };

        const handleSaveValue = () => {
            let parsedValue;
            if (paramSchema.items?.type === 'number') {
                parsedValue = parseFloat(tempValue) || 0;
            } else if (paramSchema.items?.type === 'integer') {
                parsedValue = parseInt(tempValue) || 0;
            } else {
                parsedValue = tempValue;
            }

            let newArray;
            if (editingValueIndex !== null) {
                newArray = [...arrayValue];
                newArray[editingValueIndex] = parsedValue;
            } else {
                newArray = [...arrayValue, parsedValue];
            }
            onChange(newArray);
            setAddValueModal(false);
        };

        const handleDeleteValue = (index) => {
            const newArray = arrayValue.filter((_, i) => i !== index);
            onChange(newArray);
        };

        return (
            <Stack key={paramKey} spacing={0.5}>
                <Tooltip title={paramSchema.description || ''}>
                    <FormLabel>{paramKey.replace(/_/g, ' ')}</FormLabel>
                </Tooltip>
                
                <Accordion variant="outlined">
                    <AccordionSummary>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                            <Typography level="body-md">
                                {arrayValue.length === 0 ? 'No values' : `${arrayValue.length} value${arrayValue.length !== 1 ? 's' : ''}`}
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={1}>
                            <Button 
                                startDecorator={<Add />} 
                                onClick={handleAddValue}
                                size="sm"
                                variant="soft"
                            >
                                Add New Value
                            </Button>
                            
                            {arrayValue.length > 0 && (
                                <List size="sm" sx={{ maxHeight: 200, overflow: 'auto' }}>
                                    {arrayValue.map((item, index) => (
                                        <ListItem key={index} sx={{ gap: 1 }}>
                                            <ListItemContent>
                                                <Typography level="body-sm">
                                                    {item}
                                                </Typography>
                                            </ListItemContent>
                                            <Stack direction="row" spacing={0.5}>
                                                <IconButton 
                                                    size="sm" 
                                                    variant="plain"
                                                    onClick={() => handleEditValue(index)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                    size="sm" 
                                                    variant="plain" 
                                                    color="danger"
                                                    onClick={() => handleDeleteValue(index)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                {paramSchema.description && (
                    <Typography level="body-xs" color="neutral">
                        {paramSchema.description}
                    </Typography>
                )}

                {/* Modal for adding/editing value */}
                <Modal 
                    open={addValueModal} 
                    onClose={() => setAddValueModal(false)}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <ModalDialog sx={{ minWidth: 300 }}>
                        <ModalClose onClick={() => setAddValueModal(false)} />
                        <DialogTitle>
                            {editingValueIndex !== null ? 'Edit Value' : 'Add New Value'}
                        </DialogTitle>
                        
                        <Stack spacing={2} sx={{ marginTop: 2 }}>
                            <Input
                                type={paramSchema.items?.type === 'number' || paramSchema.items?.type === 'integer' ? 'number' : 'text'}
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                                placeholder={`Enter ${paramSchema.items?.type || 'value'}`}
                                slotProps={{
                                    input: {
                                        step: paramSchema.items?.type === 'integer' ? 1 : 0.01
                                    }
                                }}
                                autoFocus
                            />
                        </Stack>

                        <Divider sx={{ marginTop: 2, marginBottom: 2 }} />

                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="outlined" onClick={() => setAddValueModal(false)}>
                                Cancel
                            </Button>
                            <Button onClick={handleSaveValue}>
                                Save
                            </Button>
                        </Stack>
                    </ModalDialog>
                </Modal>
            </Stack>
        );
    };

    // Handle array of objects
    const renderObjectArrayInput = () => {
        const arrayValue = Array.isArray(value) ? value : [];

        const handleAddItem = () => {
            setTempObjectValue({});
            setEditingIndex(null);
            setArrayItemModal(true);
        };

        const handleEditItem = (index) => {
            setTempObjectValue(arrayValue[index] || {});
            setEditingIndex(index);
            setArrayItemModal(true);
        };

        const handleDeleteItem = (index) => {
            const newArray = arrayValue.filter((_, i) => i !== index);
            onChange(newArray);
        };

        const handleSaveArrayItem = (itemValue) => {
            let newArray;
            if (editingIndex !== null) {
                newArray = [...arrayValue];
                newArray[editingIndex] = itemValue;
            } else {
                newArray = [...arrayValue, itemValue];
            }
            onChange(newArray);
            setArrayItemModal(false);
        };

        return (
            <Stack key={paramKey} spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={paramSchema.description || ''}>
                        <FormLabel>{paramKey.replace(/_/g, ' ')}</FormLabel>
                    </Tooltip>
                    <Button size="sm" startDecorator={<Add />} onClick={handleAddItem}>
                        Add Item
                    </Button>
                </Stack>
                
                {arrayValue.length > 0 && (
                    <Stack spacing={0.5} sx={{ paddingLeft: 2 }}>
                        {arrayValue.map((item, index) => (
                            <Stack key={index} direction="row" spacing={1} alignItems="center">
                                <Typography level="body-sm">
                                    Item {index + 1}: {paramSchema.items?.class || 'Object'}
                                </Typography>
                                <IconButton size="sm" onClick={() => handleEditItem(index)}>
                                    <Edit />
                                </IconButton>
                                <IconButton size="sm" color="danger" onClick={() => handleDeleteItem(index)}>
                                    <Delete />
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                )}

                {paramSchema.description && (
                    <Typography level="body-xs" color="neutral">
                        {paramSchema.description}
                    </Typography>
                )}

                {/* Modal for editing array item (object) */}
                <ObjectConfigModal
                    open={arrayItemModal}
                    onClose={() => setArrayItemModal(false)}
                    onSave={handleSaveArrayItem}
                    schema={paramSchema.item_schema}
                    title={`Configure ${paramSchema.items?.class || 'Item'}`}
                    initialValue={tempObjectValue}
                    level={level + 1}
                />
            </Stack>
        );
    };

    // Handle discriminated_union type: a choice between named strategy
    // classes (e.g. threshold_behaviour), each with its own parameters.
    const renderDiscriminatedUnionInput = () => {
        const discriminator = paramSchema.discriminator || 'kind';
        const options = paramSchema.options || {};
        const optionKeys = Object.keys(options);
        const currentKind = value?.[discriminator] !== undefined ? value[discriminator] : paramSchema.default;
        const selectedOption = options[currentKind];

        const handleKindChange = (newKind) => {
            onChange(getDiscriminatedUnionDefault({ ...paramSchema, default: newKind }));
        };

        const handleSubParamChange = (subKey, subValue) => {
            onChange({
                ...value,
                [discriminator]: currentKind,
                [subKey]: subValue
            });
        };

        return (
            <Box
                key={paramKey}
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'neutral.200',
                    borderRadius: 'md',
                    backgroundColor: 'background.surface'
                }}
            >
                <Stack spacing={1.5}>
                    <Tooltip title={paramSchema.description || ''}>
                        <FormLabel sx={{ fontWeight: 'md' }}>
                            {paramKey.replace(/_/g, ' ')}
                        </FormLabel>
                    </Tooltip>

                    <Select
                        value={currentKind}
                        onChange={(_, newKind) => newKind && handleKindChange(newKind)}
                    >
                        {optionKeys.map((key) => (
                            <Option key={key} value={key}>
                                {options[key].class || key.replace(/_/g, ' ')}
                            </Option>
                        ))}
                    </Select>

                    {selectedOption?.description && (
                        <Typography level="body-xs" color="neutral">
                            {selectedOption.description}
                        </Typography>
                    )}

                    {selectedOption?.parameters && Object.keys(selectedOption.parameters).length > 0 && (
                        <Stack spacing={2} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'neutral.200' }}>
                            {Object.entries(selectedOption.parameters).map(([subKey, subSchema]) => (
                                <Parameter
                                    key={subKey}
                                    paramKey={subKey}
                                    paramSchema={subSchema}
                                    value={value?.[subKey]}
                                    onChange={(newValue) => handleSubParamChange(subKey, newValue)}
                                    level={level + 1}
                                />
                            ))}
                        </Stack>
                    )}

                    {paramSchema.description && (
                        <Typography level="body-xs" color="neutral">
                            {paramSchema.description}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    // Handle single object type
    const renderObjectInput = () => {
        const handleOpenModal = () => {
            setTempObjectValue(value || {});
            setObjectModal(true);
        };

        const handleSaveObject = (objectValue) => {
            onChange(objectValue);
            // Auto-cache the object
            const schemaKey = getSchemaKey(paramSchema.schema);
            if (schemaKey && objectValue && Object.keys(objectValue).length > 0) {
                autoCacheObject(schemaKey, objectValue);
            }
            setObjectModal(false);
        };

        const handleCacheSelect = (cachedValue) => {
            onChange(cachedValue);
        };

        const schemaKey = getSchemaKey(paramSchema.schema);

        return (
            <Stack key={paramKey} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Tooltip title={paramSchema.description || ''}>
                        <FormLabel>{paramKey.replace(/_/g, ' ')}</FormLabel>
                    </Tooltip>
                    {schemaKey && (
                        <Button
                            size="sm"
                            variant="soft"
                            startDecorator={<ContentCopy />}
                            onClick={() => setCacheSelectorOpen(true)}
                            sx={{ minWidth: 'auto' }}
                        >
                            Cache
                        </Button>
                    )}
                </Stack>
                
                <Button variant="outlined" onClick={handleOpenModal}>
                    Configure {paramSchema.class || 'Object'}
                    {value && Object.keys(value).length > 0 ? ' ✓' : ''}
                </Button>
                
                {paramSchema.description && (
                    <Typography level="body-xs" color="neutral">
                        {paramSchema.description}
                    </Typography>
                )}

                {/* Modal for editing object */}
                <ObjectConfigModal
                    open={objectModal}
                    onClose={() => setObjectModal(false)}
                    onSave={handleSaveObject}
                    schema={paramSchema.schema}
                    title={`Configure ${paramSchema.class || 'Object'}`}
                    initialValue={tempObjectValue}
                    level={level + 1}
                />

                {/* Cache Selector */}
                {schemaKey && (
                    <ObjectCacheSelector
                        open={cacheSelectorOpen}
                        onClose={() => setCacheSelectorOpen(false)}
                        onSelect={handleCacheSelect}
                        schema={paramSchema.schema}
                        schemaKey={schemaKey}
                        title={`Select Cached ${paramSchema.class || 'Object'}`}
                    />
                )}
            </Stack>
        );
    };

    // Determine which renderer to use
    if (paramSchema.type === 'boolean') {
        return renderBooleanInput();
    } else if (paramSchema.type === 'number' || paramSchema.type === 'integer') {
        return renderNumberInput();
    } else if (paramSchema.type === 'string') {
        return renderStringInput();
    } else if (paramSchema.type === 'array') {
        if (paramSchema.items?.type === 'object') {
            return renderObjectArrayInput();
        } else {
            return renderSimpleArrayInput();
        }
    } else if (paramSchema.type === 'object') {
        return renderObjectInput();
    } else if (paramSchema.type === 'discriminated_union') {
        return renderDiscriminatedUnionInput();
    }

    // Fallback for unknown types
    return (
        <Stack key={paramKey} spacing={0.5}>
            <FormLabel>{paramKey.replace(/_/g, ' ')}</FormLabel>
            <Typography level="body-xs" color="warning">
                Unsupported parameter type: {paramSchema.type}
            </Typography>
        </Stack>
    );
}

// Recursive modal component for object configuration
function ObjectConfigModal({ open, onClose, onSave, schema, title, initialValue, level }) {
    const [objectValues, setObjectValues] = useState({});
    const [cacheSelectorOpen, setCacheSelectorOpen] = useState(false);
    const { getSchemaKey, autoCacheObject } = useObjectCache();

    // Check if this schema represents a particle (has spin, parity, and optionally mass)
    const isParticleSchema = schema && 
                             schema.spin && 
                             schema.parity && 
                             (schema.spin.type === 'number' || schema.spin.type === 'integer') &&
                             (schema.parity.type === 'integer');
    
    const hasMassField = schema && schema.mass && (schema.mass.type === 'number');

    React.useEffect(() => {
        if (schema && open) {
            setObjectValues(getDefaultValuesForSchema(schema, initialValue));
        }
    }, [schema, initialValue, open]);

    const handleParameterChange = (key, value) => {
        setObjectValues(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handlePDGParticleSelect = (particleData) => {
        const newValues = { ...objectValues };
        
        // Set spin (convert from half-integer index to actual value if needed)
        if (particleData.spin !== undefined && particleData.spin !== null) {
            newValues.spin = particleData.spin;
        }
        
        // Set parity
        if (particleData.parity !== undefined && particleData.parity !== null) {
            newValues.parity = particleData.parity;
        }
        
        // Set mass if available and field exists
        if (hasMassField && particleData.mass !== undefined && particleData.mass !== null) {
            newValues.mass = particleData.mass;
        }
        
        setObjectValues(newValues);
    };

    const handleSave = () => {
        onSave(objectValues);
        // Auto-cache the object
        const schemaKey = getSchemaKey(schema);
        if (schemaKey && objectValues && Object.keys(objectValues).length > 0) {
            autoCacheObject(schemaKey, objectValues);
        }
    };

    const handleCacheSelect = (cachedValue) => {
        setObjectValues(cachedValue);
    };

    const schemaKey = getSchemaKey(schema);

    if (!schema) return null;

    return (
        <Modal 
            open={open} 
            onClose={onClose}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
            <ModalDialog sx={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto', marginLeft: level * 2 + 'rem' }}>
                <ModalClose onClick={onClose} />
                <DialogTitle>{title}</DialogTitle>
                
                <Stack spacing={2} sx={{ marginTop: 2 }}>
                    {/* Cache Selector and PDG Searchbar */}
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography level="body-sm" color="primary" sx={{ fontWeight: 'md' }}>
                            Quick Fill Options:
                        </Typography>
                        {schemaKey && (
                            <Button
                                size="sm"
                                variant="soft"
                                startDecorator={<ContentCopy />}
                                onClick={() => setCacheSelectorOpen(true)}
                            >
                                Load from Cache
                            </Button>
                        )}
                    </Stack>

                    {/* Show PDG Searchbar if this is a particle schema */}
                    {isParticleSchema && (
                        <Stack spacing={1}>
                            <Typography level="body-sm" color="primary">
                                PDG Database:
                            </Typography>
                            <PDGSearchbar 
                                onParticleSelect={handlePDGParticleSelect}
                                includeMass={hasMassField}
                            />
                            <Divider />
                        </Stack>
                    )}

                    {Object.entries(schema).map(([key, paramSchema]) => (
                        <Parameter
                            key={key}
                            paramKey={key}
                            paramSchema={paramSchema}
                            value={objectValues[key]}
                            onChange={(newValue) => handleParameterChange(key, newValue)}
                            level={level}
                        />
                    ))}
                </Stack>

                <Divider sx={{ marginTop: 2, marginBottom: 2 }} />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave}>
                        Save
                    </Button>
                </Stack>

                {/* Cache Selector */}
                {schemaKey && (
                    <ObjectCacheSelector
                        open={cacheSelectorOpen}
                        onClose={() => setCacheSelectorOpen(false)}
                        onSelect={handleCacheSelect}
                        schema={schema}
                        schemaKey={schemaKey}
                        title={`Select Cached ${title}`}
                    />
                )}
            </ModalDialog>
        </Modal>
    );
}

